import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { IconDevice, IconCreditCard, IconLoader, IconClock, IconCheck, IconTrash, IconX } from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";
import { toast } from "../lib/toast";

interface Device {
  id: string;
  name: string;
  platform: string | null;
  app_version: string | null;
  branch_id: string | null;
  is_active: boolean;
  registered_at: string | null;
  last_seen_at: string | null;
  last_activity_at: string | null;
  license_type: string | null;
  license_period: string | null;
  license_expires_at: string | null;
  subscription_status: string | null;
  subscription_period: string | null;
  subscription_cancel_at_period_end: boolean | null;
  subscription_current_period_end: string | null;
  has_fiscal_history: boolean;
}

function PlatformIcon({ platform, size = 14 }: { platform: string | null; size?: number }) {
  const p = platform || "web";
  const cls =
    p === "android" ? "devicon-android-plain" :
    p === "ios" ? "devicon-apple-original" :
    p === "windows" ? "devicon-windows8-original" :
    "devicon-chrome-plain";
  return <i className={cls} style={{ fontSize: size }} title={p} />;
}

export default function Devices() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const canManage = !!user && (user.role === "owner" || user.role === "manager");
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const setPaymentMsg = (m: { type: "success" | "error"; text: string } | null) => {
    if (m) toast(m.type === "success" ? "ok" : "err", m.text);
  };
  const [transferFor, setTransferFor] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState<string>("");
  const [subscribeFor, setSubscribeFor] = useState<{ device: Device; period: "month" | "year" } | null>(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/licenses/devices");
      setDevices(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  // Handle Stripe checkout return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    if (!payment) return;
    const sessionId = params.get("session_id");
    if (payment === "cancelled") {
      setPaymentMsg({ type: "error", text: t("devices.paymentCancelled") });
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }
    if (payment !== "success") return;
    if (!sessionId) {
      // Webhook activates the license — confirm and refresh (twice, the
      // webhook may lag a few seconds behind the redirect).
      setPaymentMsg({ type: "success", text: t("devices.paymentOk") });
      void load();
      setTimeout(() => void load(), 4000);
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }
    void (async () => {
      try {
        const { data } = await api.post("/licenses/complete-purchase", { session_id: sessionId });
        setPaymentMsg({ type: "success", text: data.ok === "already_activated" ? t("devices.licenseAlreadyActive") : t("devices.licenseActivated") });
        await load();
      } catch (e: any) {
        setPaymentMsg({ type: "error", text: e?.response?.data?.detail || t("devices.activationError") });
      }
      window.history.replaceState(null, "", window.location.pathname);
    })();
  }, [t]);

  async function subscribe(device: Device, period: "month" | "year") {
    setBusy(`buy:${device.id}`);
    try {
      const { data } = await api.post(`/licenses/devices/${device.id}/subscribe`, { period });
      window.location.href = data.checkout_url;
    } catch (e: any) {
      alert(e?.response?.data?.detail || t("devices.buyError"));
      setBusy(null);
      setSubscribeFor(null);
    }
  }

  async function subscriptionAction(device: Device, action: "cancel" | "resume" | "change-period", period?: "month" | "year") {
    setBusy(`sub:${device.id}`);
    try {
      if (action === "change-period") {
        await api.post(`/licenses/devices/${device.id}/subscription/change-period`, { period });
      } else {
        await api.post(`/licenses/devices/${device.id}/subscription/${action}`);
      }
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || t("devices.actionError"));
    } finally {
      setBusy(null);
    }
  }

  async function openPortal(device: Device) {
    setBusy(`sub:${device.id}`);
    try {
      const { data } = await api.post(`/licenses/devices/${device.id}/subscription/portal`);
      window.location.href = data.portal_url;
    } catch (e: any) {
      alert(e?.response?.data?.detail || t("devices.actionError"));
      setBusy(null);
    }
  }

  async function setActive(device: Device, isActive: boolean) {
    if (!isActive && !confirm(t("devices.confirmUnregister", { name: device.name }))) return;
    setBusy(`active:${device.id}`);
    try {
      await api.put(`/licenses/devices/${device.id}/active`, { is_active: isActive });
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || t("devices.actionError"));
    } finally {
      setBusy(null);
    }
  }

  async function transferLicense(source: Device) {
    if (!transferTarget) return;
    const target = devices.find((d) => d.id === transferTarget);
    if (!target) return;
    if (!confirm(t("devices.confirmTransfer", { source: source.name, target: target.name }))) return;
    setBusy(`transfer:${source.id}`);
    try {
      await api.post(`/licenses/devices/${source.id}/transfer-license`, { target_device_id: transferTarget });
      setTransferFor(null);
      setTransferTarget("");
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || t("devices.transferError"));
    } finally {
      setBusy(null);
    }
  }

  async function deleteDevice(device: Device) {
    const confirmKey = device.has_fiscal_history ? "devices.confirmDeleteHistory" : "devices.confirmDelete";
    if (!confirm(t(confirmKey, { name: device.name }))) return;
    setBusy(`delete:${device.id}`);
    try {
      await api.delete(`/licenses/devices/${device.id}`);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || t("devices.deleteError"));
    } finally {
      setBusy(null);
    }
  }

  const fmt = (d: string | null) => d ? new Date(d).toLocaleString("cs-CZ") : "—";
  const isExpired = (d: string | null) => d ? new Date(d).getTime() <= Date.now() : true;
  const daysLeft = (d: string | null) => {
    if (!d) return null;
    const diff = new Date(d).getTime() - Date.now();
    return Math.ceil(diff / 86400000);
  };

  const transferOptions = (sourceId: string) =>
    devices
      .filter((d) => d.id !== sourceId)
      .map((d) => ({ value: d.id, label: `${d.name}${d.is_active ? "" : ` (${t("common.inactive").toLowerCase()})`}` }));

  return (
    <div>
      <div className="page-header">
        <h1>{t("devices.title")}</h1>
        <p>{t("devices.subtitle")}</p>
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: "center", padding: 48 }}>
          <IconLoader size={32} className="muted" />
          <p style={{ color: "var(--muted)", marginTop: 12 }}>{t("devices.loading")}</p>
        </div>
      ) : devices.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48 }}>
          <IconDevice size={48} className="muted" />
          <h3 style={{ marginTop: 16, fontSize: 16, fontWeight: 600 }}>{t("devices.empty")}</h3>
          <p style={{ color: "var(--muted)", marginTop: 8, fontSize: 14 }}>
            {t("devices.emptyHint")}
          </p>
        </div>
      ) : (
        <div className="grid grid-2">
          {devices.map((d) => {
            const expired = isExpired(d.license_expires_at);
            const days = daysLeft(d.license_expires_at);
            const isTrial = d.license_period === "trial";
            const isBuyBusy = busy === `buy:${d.id}`;
            const isActiveBusy = busy === `active:${d.id}`;
            const isTransferBusy = busy === `transfer:${d.id}`;
            const isDeleteBusy = busy === `delete:${d.id}`;
            const isSubBusy = busy === `sub:${d.id}`;
            const hasSub = !!d.subscription_status;
            const subCancelling = !!d.subscription_cancel_at_period_end;
            // paid + operator-managed (invoice/promo) licenses can be
            // transferred and block device deletion, mirroring the backend.
            const hasActiveLicense = !expired
              && (d.license_type === "paid" || d.license_type === "invoice" || d.license_type === "promo");
            return (
              <div key={d.id} className="card" style={{ position: "relative", overflow: "visible" }}>
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 3,
                  background: expired ? "var(--red)" : isTrial ? "var(--gradient-warm)" : "var(--gradient-sunset)",
                }} />

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                  <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: "var(--radius-sm)",
                      background: "var(--bg-elevated)", border: "1px solid var(--border-light)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "var(--muted)",
                    }}>
                      <PlatformIcon platform={d.platform} size={24} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: 16, fontWeight: 600 }}>{d.name}</h3>
                      <div style={{ display: "flex", gap: 8, marginTop: 4, alignItems: "center" }}>
                        <span style={{ color: "var(--muted)", fontSize: 12 }}>
                          {d.app_version ? `v${d.app_version}` : ""}
                        </span>
                        {d.is_active
                          ? <span className="badge badge-green">{t("common.active")}</span>
                          : <span className="badge badge-red">{t("common.inactive")}</span>}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{
                  padding: "14px 16px", borderRadius: "var(--radius-sm)",
                  background: "var(--bg-elevated)", border: "1px solid var(--border)",
                  marginBottom: 16,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{t("devices.license")}</span>
                    {!d.license_type
                      ? <span className="badge badge-red">{t("dashboard.noLicense")}</span>
                      : expired
                      ? <span className="badge badge-red">{t("devices.expired")}</span>
                      : d.license_type === "invoice" || d.license_type === "promo"
                      ? <span className="badge badge-accent">
                          {`${t(`devices.type.${d.license_type}`)} · ${t(`devices.period.${d.license_period ?? "none"}`)}`}
                        </span>
                      : null}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                      <IconClock size={16} className="muted" />
                      <span style={{ color: expired ? "var(--red)" : "var(--text)" }}>
                        {fmt(d.license_expires_at)}
                      </span>
                    </div>
                    {!expired && days !== null && (
                      <span style={{ fontSize: 13, color: days <= 7 ? "var(--yellow)" : "var(--muted)" }}>
                        {t("devices.daysLeft", { count: days })}
                      </span>
                    )}
                  </div>
                  <div style={{
                    display: "flex", gap: 16, marginTop: 10, paddingTop: 10,
                    borderTop: "1px solid var(--border)", fontSize: 12, color: "var(--muted)",
                  }}>
                    <div>
                      <span style={{ display: "block", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, fontSize: 10 }}>{t("devices.registered")}</span>
                      <span style={{ color: "var(--text-secondary)" }}>{fmt(d.registered_at)}</span>
                    </div>
                    <div>
                      <span style={{ display: "block", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, fontSize: 10 }}>{t("devices.lastActivity")}</span>
                      <span style={{ color: "var(--text-secondary)" }}>{fmt(d.last_activity_at)}</span>
                    </div>
                  </div>
                </div>

                {hasSub && (
                <div style={{
                  padding: "14px 16px", borderRadius: "var(--radius-sm)",
                  background: "var(--bg-elevated)", border: "1px solid var(--border)",
                  marginBottom: 16,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{t("devices.subscription")}</span>
                    {subCancelling
                      ? <span className="badge badge-yellow">{t("devices.subCancelling")}</span>
                      : d.subscription_status === "trialing"
                      ? <span className="badge badge-yellow">{t("devices.subPending")}</span>
                      : d.subscription_status === "past_due" || d.subscription_status === "unpaid"
                      ? <span className="badge badge-red">{t("devices.subFailed")}</span>
                      : <span className="badge badge-accent">{t(`devices.period.${d.subscription_period ?? "none"}`)}</span>}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                      <IconCreditCard size={16} className="muted" />
                      <span>
                        {d.subscription_status === "trialing"
                          ? t("devices.subFirstCharge", { date: fmt(d.subscription_current_period_end) })
                          : subCancelling
                          ? t("devices.subEnds", { date: fmt(d.license_expires_at) })
                          : t("devices.subNextCharge", { date: fmt(d.subscription_current_period_end) })}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                    {!subCancelling && d.subscription_status !== "trialing" && (
                      <button
                        onClick={() => {
                          const target = d.subscription_period === "month" ? "year" : "month";
                          if (confirm(t("devices.confirmChangePeriod", { period: t(`devices.period.${target}`).toLowerCase() })))
                            void subscriptionAction(d, "change-period", target);
                        }}
                        disabled={!!busy}
                      >
                        {isSubBusy ? <IconLoader size={16} /> : t(d.subscription_period === "month" ? "devices.switchToYear" : "devices.switchToMonth")}
                      </button>
                    )}
                    <button onClick={() => void openPortal(d)} disabled={!!busy}>
                      {isSubBusy ? <IconLoader size={16} /> : t("devices.manageBilling")}
                    </button>
                    {subCancelling ? (
                      <button className="primary" onClick={() => void subscriptionAction(d, "resume")} disabled={!!busy}>
                        {isSubBusy ? <IconLoader size={16} /> : t("devices.resumeSub")}
                      </button>
                    ) : (
                      <button className="danger" onClick={() => { if (confirm(t("devices.confirmCancelSub"))) void subscriptionAction(d, "cancel"); }} disabled={!!busy}>
                        {isSubBusy ? <IconLoader size={16} /> : t("devices.cancelSub")}
                      </button>
                    )}
                  </div>
                </div>
                )}

                {canManage && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {!hasSub && (
                  <>
                  <button onClick={() => setSubscribeFor({ device: d, period: "month" })} disabled={!!busy} style={{ flex: 1 }}>
                    {isBuyBusy ? <IconLoader size={16} /> : <><IconCreditCard size={16} /> {t("devices.buyMonth")}</>}
                  </button>
                  <button className="primary" onClick={() => setSubscribeFor({ device: d, period: "year" })} disabled={!!busy} style={{ flex: 1 }}>
                    {isBuyBusy ? <IconLoader size={16} /> : <><IconCreditCard size={16} /> {t("devices.buyYear")}</>}
                  </button>
                  </>
                  )}
                  {d.is_active ? (
                    <button className="danger" onClick={() => setActive(d, false)} disabled={!!busy} title={t("devices.unregisterTitle")}>
                      {isActiveBusy ? <IconLoader size={16} /> : <><IconTrash size={16} /> {t("devices.unregister")}</>}
                    </button>
                  ) : (
                    <>
                      <button onClick={() => setActive(d, true)} disabled={!!busy} title={t("devices.reactivateTitle")}>
                        {isActiveBusy ? <IconLoader size={16} /> : <><IconCheck size={16} /> {t("common.activate")}</>}
                      </button>
                      {!hasActiveLicense && (
                        <button
                          className="danger"
                          onClick={() => deleteDevice(d)}
                          disabled={!!busy}
                          title={d.has_fiscal_history ? t("devices.deleteHistoryTitle") : t("devices.deleteTitle")}
                        >
                          {isDeleteBusy ? <IconLoader size={16} /> : <><IconTrash size={16} /> {t("common.delete")}</>}
                        </button>
                      )}
                    </>
                  )}
                  {hasActiveLicense && !hasSub && (
                    <button onClick={() => { setTransferFor(d.id); setTransferTarget(""); }} disabled={!!busy} style={{ flex: 1 }}>
                      {t("devices.transferLicense")}
                    </button>
                  )}
                </div>
                )}

                {canManage && transferFor === d.id && (
                  <div style={{
                    marginTop: 16,
                    padding: 16,
                    borderRadius: "var(--radius-sm)",
                    background: "var(--bg-elevated)",
                    border: "1px solid var(--border)",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <strong style={{ fontSize: 14 }}>{t("devices.transferTo")}</strong>
                      <button className="ghost" onClick={() => setTransferFor(null)} style={{ padding: 4 }}>
                        <IconX size={18} />
                      </button>
                    </div>
                    <SearchableSelect
                      value={transferTarget}
                      onChange={setTransferTarget}
                      options={transferOptions(d.id)}
                      placeholder={t("devices.selectTarget")}
                    />
                    <button
                      className="primary"
                      style={{ marginTop: 12, width: "100%" }}
                      disabled={!transferTarget || isTransferBusy}
                      onClick={() => transferLicense(d)}
                    >
                      {isTransferBusy ? <IconLoader size={16} /> : t("devices.confirmTransferBtn")}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {subscribeFor && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={() => setSubscribeFor(null)}
        >
          <div
            className="card"
            style={{ width: "100%", maxWidth: 460, padding: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>{t("devices.subscribeTitle")}</h2>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6 }}>
              {t("devices.subscribeInfo")}
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 24 }}>
              <button onClick={() => setSubscribeFor(null)} disabled={!!busy} style={{ flex: 1 }}>
                {t("devices.subscribeCancel")}
              </button>
              <button
                className="primary"
                onClick={() => void subscribe(subscribeFor.device, subscribeFor.period)}
                disabled={!!busy}
                style={{ flex: 1 }}
              >
                {busy === `buy:${subscribeFor.device.id}` ? <IconLoader size={16} /> : t("devices.subscribeContinue")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
